import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import client from '../../API';
import { checkHasAuthToken } from '../../API/token-util';
import { getAuthToken as getCreatorAuthToken } from '../../API/creatorTokenUtil';

const SENTINEL_KEY = 'stripe_onboarding';
const SENTINEL_VALUE = 'complete';

const hasAnyAuthToken = () => Boolean(checkHasAuthToken() || getCreatorAuthToken());

const StripeOnboardingReturnHandler = () => {
  const location = useLocation();
  const handledRef = useRef(false);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get(SENTINEL_KEY) !== SENTINEL_VALUE) return;
    if (handledRef.current) return;
    handledRef.current = true;

    // Run immediately if auth is ready; on Capacitor the token stores hydrate
    // asynchronously so we give them up to 2 s before giving up.
    const attempt = async () => {
      if (!hasAnyAuthToken()) {
        stripSentinel();
        return;
      }

      try {
        const result = await client.bank.reconcileExternalAccounts();
        if (result?.completed === false) {
          toast.info('Stripe onboarding was not completed — finish to enable payouts.');
        } else {
          const count = result?.reconciled || 0;
          if (count > 0) {
            toast.success(
              count === 1
                ? 'Bank account synced from Stripe onboarding'
                : `${count} bank accounts synced from Stripe onboarding`
            );
          }
        }
      } catch {
        // Silent — user can re-link via the bank page if needed.
      } finally {
        stripSentinel();
      }
    };

    if (hasAnyAuthToken()) {
      attempt();
    } else {
      // Auth stores may still be hydrating (Capacitor async init). Retry once
      // after a short delay; if still no token, silently strip the sentinel.
      const retryTimer = setTimeout(() => {
        if (hasAnyAuthToken()) {
          attempt();
        } else {
          stripSentinel();
        }
      }, 500);
      return () => clearTimeout(retryTimer);
    }
  }, [location.search]);

  return null;
};

const stripSentinel = () => {
  try {
    const url = new URL(window.location.href);
    url.searchParams.delete(SENTINEL_KEY);
    window.history.replaceState({}, '', url.toString());
  } catch {
    // No-op on environments without URL/history (SSR, tests).
  }
};

export default StripeOnboardingReturnHandler;
